import { z } from "zod";

import { pageRefSchema, provenanceTypeSchema, reviewStateSchema } from "../schema";

const sceneIdSchema = z.enum(["M6S1", "M6S2", "M6S3"]);
const taskIdSchema = z.enum(["T18", "T19"]);
const nodeIdSchema = z.string().regex(/^M6S[1-3]_N0[1-4]$/);
const claimIdSchema = z.string().regex(/^M6S[1-3]_CLAIM_[A-Z0-9_]+$/);

const sourceAttributionSchema = z.object({
  sourceRefs: z.tuple([z.literal("SRC_TEXTBOOK_CH3")]),
  provenanceType: provenanceTypeSchema,
  sourceCategory: z.literal("教材正文"),
  citationLabel: z.literal("《中国共产党简史》第三章"),
  pageRefs: z.array(pageRefSchema).length(1),
});

const assetSuggestionSchema = z.object({
  id: z.string().regex(/^AST-M6S[1-3]-SUG-[0-9]{3}$/),
  kind: z.enum(["archive_media", "interface", "programmatic_2_5d", "programmatic_3d"]),
  label: z.string().min(1),
  purpose: z.string().min(1),
  representationLabel: z.enum([
    "复原性创作·非档案影像",
    "教材载述史料·非档案原件",
    "档案素材建议·待来源与权利核验",
  ]),
  rightsPlan: z.enum(["original_procedural", "pending_source_and_rights_review"]),
});

const interactionSuggestionSchema = z.object({
  mode: z.enum([
    "guided_archive_hall",
    "document_evidence_reader",
    "policy_comparison",
    "organization_evidence_map",
    "theory_scope_matrix",
    "historical_significance_chain",
    "counteroffensive_map",
    "victory_date_timeline",
    "solemn_statistical_witness",
    "central_pillar_evidence_chain",
  ]),
  learnerAction: z.string().min(1),
  feedback: z.string().min(1),
  nonScored: z.literal(true),
  historyConstraint: z.literal("只核验既定史实，不改变历史结果"),
  keyboardOperable: z.literal(true),
});

const renderSchema = z.object({
  kind: z.enum([
    "congress_archive_hall",
    "report_document_wall",
    "policy_program_matrix",
    "organization_evidence_map",
    "theory_document_reader",
    "theory_scope_matrix",
    "historical_significance_chain",
    "counteroffensive_map",
    "victory_date_timeline",
    "solemn_statistical_witness",
    "central_pillar_evidence_chain",
  ]),
  visualSummary: z.string().min(1),
  interaction: interactionSuggestionSchema,
  guardrails: z.array(z.string().min(1)).min(1),
});

const verbatimQuoteSchema = z.object({
  text: z.string().min(1),
  context: z.string().min(1),
  verbatim: z.literal(true),
});

const dateMarkerSchema = z.object({
  date: z.string().regex(/^1945-(0[1-9]|1[0-2])-([0-2][0-9]|3[01])$/),
  category: z.enum([
    "counteroffensive",
    "surrender_broadcast",
    "surrender_signature",
    "victory_memorial_day",
    "taiwan_surrender_ceremony",
  ]),
  label: z.string().min(1),
});

export const m6BlueprintNodeSchema = z
  .object({
    id: nodeIdSchema,
    sceneId: sceneIdSchema,
    order: z.number().int().positive(),
    nodeType: z.enum(["lecture", "archive", "witness"]),
    title: z.string().min(1),
    timeAnchor: z.string().min(1),
    estimatedSeconds: z.number().int().positive(),
    claimId: claimIdSchema,
    claim: z.string().min(1),
    evidencePoints: z.array(z.string().min(1)).min(1),
    verbatimQuotes: z.array(verbatimQuoteSchema),
    dateMarkers: z.array(dateMarkerSchema).optional(),
    assetSuggestions: z.array(assetSuggestionSchema).min(1),
    source: sourceAttributionSchema,
    sensitivity: z.enum(["standard", "solemn_sacrifice"]),
    render: renderSchema,
    reviewState: reviewStateSchema,
    reviewNote: z.string().min(1).optional(),
  })
  .superRefine((node, context) => {
    if (!node.id.startsWith(`${node.sceneId}_`)) {
      context.addIssue({
        code: "custom",
        message: `${node.id}必须归属于其sceneId。`,
        path: ["id"],
      });
    }
    if (!node.claimId.startsWith(`${node.sceneId}_`)) {
      context.addIssue({
        code: "custom",
        message: `${node.claimId}必须归属于其sceneId。`,
        path: ["claimId"],
      });
    }
    if (
      node.source.provenanceType !== "textbook" ||
      node.source.sourceRefs[0] !== "SRC_TEXTBOOK_CH3"
    ) {
      context.addIssue({
        code: "custom",
        message: `${node.id}必须明确归属教材第三章。`,
        path: ["source"],
      });
    }
    if (node.sensitivity === "solemn_sacrifice") {
      const requiredGuardrails = [
        "采用庄重统计史料见证",
        "不把伤亡与损失制作成战果积分",
        "不使用庆祝动画覆盖牺牲叙事",
        "不呈现猎奇或伤害特写",
      ];
      if (node.nodeType !== "witness" || node.render.kind !== "solemn_statistical_witness") {
        context.addIssue({
          code: "custom",
          message: `${node.id}的牺牲与损失内容必须使用庄重统计史料见证节点。`,
          path: ["render"],
        });
      }
      for (const guardrail of requiredGuardrails) {
        if (!node.render.guardrails.includes(guardrail)) {
          context.addIssue({
            code: "custom",
            message: `${node.id}缺少牺牲史料呈现底线：${guardrail}。`,
            path: ["render", "guardrails"],
          });
        }
      }
    }
    if (node.dateMarkers) {
      const dates = node.dateMarkers.map((marker) => marker.date);
      if (new Set(dates).size !== dates.length) {
        context.addIssue({
          code: "custom",
          message: `${node.id}的胜利日期标记必须唯一。`,
          path: ["dateMarkers"],
        });
      }
    }
  });

const sceneReviewNoteSchema = z.object({
  id: z.string().regex(/^M6S[1-3]_REVIEW_[A-Z0-9_]+$/),
  scope: z.string().min(1),
  state: z.literal("open"),
  note: z.string().min(1),
});

export const m6SceneBlueprintSchema = z
  .object({
    id: sceneIdSchema,
    order: z.number().int().min(1).max(3),
    title: z.string().min(1),
    identity: z.literal("抗战史料研学员"),
    timeLabel: z.string().min(1),
    mainVenue: z.string().min(1),
    archiveSlices: z.array(z.string().min(1)),
    pageRefs: z.array(pageRefSchema).length(1),
    lectureBudgetSeconds: z.number().int().positive(),
    formalTaskIds: z.array(taskIdSchema).max(1),
    taskAnchorNodeId: nodeIdSchema.optional(),
    sequence: z.array(z.string().regex(/^(M6S[1-3]_N0[1-4]|T18|T19)$/)).min(3),
    localExtensionIds: z.array(z.string()).length(0),
    nodes: z.array(m6BlueprintNodeSchema).min(3).max(4),
    reviewState: reviewStateSchema,
    reviewNotes: z.array(sceneReviewNoteSchema),
  })
  .superRefine((scene, context) => {
    const nodeIds = scene.nodes.map((node) => node.id);
    const claimIds = scene.nodes.map((node) => node.claimId);
    const expectedSequence = [...nodeIds, ...scene.formalTaskIds];

    if (scene.nodes.some((node) => node.sceneId !== scene.id)) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}只能包含本场景节点。`,
        path: ["nodes"],
      });
    }
    if (new Set(nodeIds).size !== nodeIds.length || new Set(claimIds).size !== claimIds.length) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}节点ID和claimId必须唯一。`,
        path: ["nodes"],
      });
    }
    if (scene.nodes.some((node, index) => node.order !== (index + 1) * 10)) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}节点必须按10递增声明。`,
        path: ["nodes"],
      });
    }
    if (JSON.stringify(scene.sequence) !== JSON.stringify(expectedSequence)) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}序列必须按声明顺序完整包含主体节点，并在主体后直接进入正式任务。`,
        path: ["sequence"],
      });
    }
    if (
      scene.nodes.reduce((total, node) => total + node.estimatedSeconds, 0) !==
      scene.lectureBudgetSeconds
    ) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}节点时长合计必须等于场景讲授预算。`,
        path: ["lectureBudgetSeconds"],
      });
    }
    if (scene.formalTaskIds.length === 0 && scene.taskAnchorNodeId !== undefined) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}没有正式任务，不得配置任务锚点。`,
        path: ["taskAnchorNodeId"],
      });
    }
    if (
      scene.formalTaskIds.length === 1 &&
      (!scene.taskAnchorNodeId || !nodeIds.includes(scene.taskAnchorNodeId))
    ) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}正式任务必须锚定本场景内容节点。`,
        path: ["taskAnchorNodeId"],
      });
    }
  });

const moduleReviewNoteSchema = z.object({
  id: z.string().regex(/^M6_REVIEW_[A-Z0-9_]+$/),
  scope: z.string().min(1),
  state: z.literal("open"),
  note: z.string().min(1),
});

export const m6BlueprintSchema = z
  .object({
    id: z.literal("M6"),
    version: z.literal("1.0.0"),
    title: z.literal("党的七大和确立毛泽东思想为党的指导思想、抗日战争的最后胜利"),
    identity: z.literal("抗战史料研学员"),
    printPageRange: z.literal("103—110"),
    pdfPageRange: z.literal("123—130"),
    sourceRefs: z.tuple([z.literal("SRC_TEXTBOOK_CH3")]),
    taskIds: z.tuple([z.literal("T18"), z.literal("T19")]),
    scenes: z.array(m6SceneBlueprintSchema).length(3),
    reviewState: reviewStateSchema,
    reviewNotes: z.array(moduleReviewNoteSchema).min(1),
  })
  .superRefine((blueprint, context) => {
    const expectedSceneIds = ["M6S1", "M6S2", "M6S3"];
    const expectedTaskMapping: Readonly<Record<string, readonly string[]>> = {
      M6S1: ["T18"],
      M6S2: [],
      M6S3: ["T19"],
    };

    if (
      blueprint.scenes.some(
        (scene, index) => scene.id !== expectedSceneIds[index] || scene.order !== index + 1,
      )
    ) {
      context.addIssue({
        code: "custom",
        message: "模块六必须固定保留M6S1—M6S3三场景及其顺序。",
        path: ["scenes"],
      });
    }
    for (const [index, scene] of blueprint.scenes.entries()) {
      const expectedTasks = expectedTaskMapping[scene.id] ?? [];
      if (JSON.stringify(scene.formalTaskIds) !== JSON.stringify(expectedTasks)) {
        context.addIssue({
          code: "custom",
          message: `${scene.id}正式任务映射不得改变。`,
          path: ["scenes", index, "formalTaskIds"],
        });
      }
    }
  });

export type M6BlueprintNode = z.infer<typeof m6BlueprintNodeSchema>;
export type M6SceneBlueprint = z.infer<typeof m6SceneBlueprintSchema>;
export type M6Blueprint = z.infer<typeof m6BlueprintSchema>;

const textbookSource = (print: string, pdf: string): M6BlueprintNode["source"] => ({
  sourceRefs: ["SRC_TEXTBOOK_CH3"],
  provenanceType: "textbook",
  sourceCategory: "教材正文",
  citationLabel: "《中国共产党简史》第三章",
  pageRefs: [{ print, pdf }],
});

const standardGuardrails: string[] = [
  "不模拟学生改变历史结果",
  "不使用战果积分或娱乐化庆祝",
  "主场域与离散档案切片明确分层",
];
const sacrificeGuardrails: string[] = [
  "采用庄重统计史料见证",
  "不把伤亡与损失制作成战果积分",
  "不使用庆祝动画覆盖牺牲叙事",
  "不呈现猎奇或伤害特写",
];

export const m6Blueprint = {
  id: "M6",
  version: "1.0.0",
  title: "党的七大和确立毛泽东思想为党的指导思想、抗日战争的最后胜利",
  identity: "抗战史料研学员",
  printPageRange: "103—110",
  pdfPageRange: "123—130",
  sourceRefs: ["SRC_TEXTBOOK_CH3"],
  taskIds: ["T18", "T19"],
  scenes: [
    {
      id: "M6S1",
      order: 1,
      title: "党的七大",
      identity: "抗战史料研学员",
      timeLabel: "1945年4—6月·中共七大召开期间",
      mainVenue: "延安杨家岭中央大礼堂",
      archiveSlices: ["七大报告", "政治路线", "三大作风档案"],
      pageRefs: [{ print: "103—105", pdf: "123—125" }],
      lectureBudgetSeconds: 240,
      formalTaskIds: ["T18"],
      taskAnchorNodeId: "M6S1_N02",
      sequence: ["M6S1_N01", "M6S1_N02", "M6S1_N03", "M6S1_N04", "T18"],
      localExtensionIds: [],
      nodes: [
        {
          id: "M6S1_N01",
          sceneId: "M6S1",
          order: 10,
          nodeType: "archive",
          title: "在胜利前夜召开的七大",
          timeAnchor: "1945年4—6月",
          estimatedSeconds: 55,
          claimId: "M6S1_CLAIM_CONGRESS_CONVENED",
          claim:
            "在德国法西斯覆亡和抗日战争接近胜利的前夜，中国共产党第七次全国代表大会在延安召开，承担总结经验、迎接胜利和引导中国前途的任务。",
          evidencePoints: [
            "1945年4月至6月，中国共产党第七次全国代表大会在延安杨家岭中央大礼堂召开。",
            "出席大会的正式代表547人、候补代表208人，代表全国121万名党员。",
            "毛泽东提交《论联合政府》政治报告并作口头报告，朱德作《论解放区战场》军事报告，刘少奇作《关于修改党章的报告》，周恩来作《论统一战线》发言。",
          ],
          verbatimQuotes: [],
          assetSuggestions: [
            {
              id: "AST-M6S1-SUG-001",
              kind: "archive_media",
              label: "党的七大会场档案照片建议",
              purpose: "以教材所载会场照片和代表数据建立大会规模感，照片须保留来源与权利状态。",
              representationLabel: "档案素材建议·待来源与权利核验",
              rightsPlan: "pending_source_and_rights_review",
            },
          ],
          source: textbookSource("103", "123"),
          sensitivity: "standard",
          render: {
            kind: "congress_archive_hall",
            visualSummary:
              "在场景化礼堂框架内分层呈现会场照片、代表人数和四份报告，不把学习者设置为真实代表。",
            interaction: {
              mode: "guided_archive_hall",
              learnerAction: "用键盘或指针依次开启代表构成与四份报告档案位。",
              feedback: "完成核验后归纳大会召开的历史时点与核心任务。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
              keyboardOperable: true,
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
        {
          id: "M6S1_N02",
          sceneId: "M6S1",
          order: 20,
          nodeType: "archive",
          title: "政治路线与联合政府主张",
          timeAnchor: "1945年4—6月",
          estimatedSeconds: 65,
          claimId: "M6S1_CLAIM_POLITICAL_LINE_AND_UNION_GOVERNMENT",
          claim: "七大提出党的政治路线，和结束国民党一党专政的两个具体步骤。",
          evidencePoints: [
            "政治路线同时包含放手发动群众、壮大人民力量、党的领导、打败侵略者、解放全国人民和建立新民主主义中国。",
            "大会提出结束国民党一党专政的两个具体步骤：当前成立临时联合政府，将来经自由而无拘束的选举召开国民大会，成立正式联合政府。",
          ],
          verbatimQuotes: [
            {
              text: "放手发动群众，壮大人民力量，在我党的领导下，打败日本侵略者，解放全国人民，建立一个新民主主义的中国。",
              context: "党的七大提出的政治路线",
              verbatim: true,
            },
          ],
          assetSuggestions: [
            {
              id: "AST-M6S1-SUG-002",
              kind: "interface",
              label: "七大政治路线证据卷轴",
              purpose: "按六个不可删减的语义单元展开政治路线，并连接联合政府两步主张。",
              representationLabel: "教材载述史料·非档案原件",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("103—104", "123—124"),
          sensitivity: "standard",
          render: {
            kind: "report_document_wall",
            visualSummary: "政治路线原文置于中央，联合政府当前与将来两个步骤作为可比较证据卡。",
            interaction: {
              mode: "document_evidence_reader",
              learnerAction: "逐段点亮政治路线语义单元，再展开联合政府的两个步骤。",
              feedback: "核验完整表述后开放T18；答错时回到政治路线原文，不改题目。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
              keyboardOperable: true,
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
        {
          id: "M6S1_N03",
          sceneId: "M6S1",
          order: 30,
          nodeType: "lecture",
          title: "新民主主义纲领、工业化任务与生产力标准",
          timeAnchor: "1945年4—6月",
          estimatedSeconds: 60,
          claimId: "M6S1_CLAIM_STATE_PROGRAM_AND_PRODUCTIVITY",
          claim:
            "七大制定新民主主义国家在政治、经济、文化方面的纲领，提出中国工业化任务，并以是否解放和发展生产力衡量政党政策及实践。",
          evidencePoints: [
            "大会制定新民主主义国家在政治、经济、文化方面的纲领。",
            "大会提出实现中国工业化的宏伟任务。",
            "党的文件首次明确提出，以是否帮助中国人民生产力发展、是束缚还是解放生产力衡量政党政策及其实践。",
          ],
          verbatimQuotes: [
            {
              text: "中国一切政党的政策及其实践在中国人民中所表现的作用的好坏、大小，归根到底，看它对于中国人民的生产力的发展是否有帮助及其帮助之大小，看它是束缚生产力的，还是解放生产力的。",
              context: "七大文件关于判断政党政策及实践作用的标准",
              verbatim: true,
            },
          ],
          assetSuggestions: [
            {
              id: "AST-M6S1-SUG-003",
              kind: "interface",
              label: "纲领与生产力标准对照矩阵",
              purpose: "把政治、经济、文化纲领、工业化任务与生产力标准组织为可核验关系。",
              representationLabel: "教材载述史料·非档案原件",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("104", "124"),
          sensitivity: "standard",
          render: {
            kind: "policy_program_matrix",
            visualSummary: "三类国家纲领、工业化目标和生产力判断标准形成由主张到尺度的证据矩阵。",
            interaction: {
              mode: "policy_comparison",
              learnerAction: "展开三类纲领并把工业化任务、生产力标准放入对应证据层。",
              feedback: "形成七大对国家建设方向与政策评价标准的结构化认识。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
              keyboardOperable: true,
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
        {
          id: "M6S1_N04",
          sceneId: "M6S1",
          order: 40,
          nodeType: "lecture",
          title: "三大作风与空前团结统一",
          timeAnchor: "1945年4—8月",
          estimatedSeconds: 60,
          claimId: "M6S1_CLAIM_WORK_STYLES_AND_ORGANIZATIONAL_UNITY",
          claim:
            "七大概括党的三大优良作风，并通过看齐原则和新的中央委员会选举使全党在组织上达到空前团结统一。",
          evidencePoints: [
            "三大作风是理论和实践相结合、和人民群众紧密联系在一起、自我批评的作风，是共产党区别于其他政党的显著标志。",
            "七大选举产生新的中央委员会；6月19日，七届一中全会选出13名中央政治局委员。",
            "毛泽东、朱德、刘少奇、周恩来、任弼时为中央书记处书记，毛泽东为中央委员会主席、中央政治局主席、中央书记处主席；8月中央政治局会议决定毛泽东为中央军事委员会主席。",
            "七大以“团结的大会，胜利的大会”载入党的史册。",
          ],
          verbatimQuotes: [
            {
              text: "我们要向中央基准看齐，向大会基准看齐。看齐是原则，有偏差是实际生活，有了偏差，就喊看齐。",
              context: "毛泽东在七大预备会议上关于看齐意识的讲话",
              verbatim: true,
            },
          ],
          assetSuggestions: [
            {
              id: "AST-M6S1-SUG-004",
              kind: "programmatic_2_5d",
              label: "三大作风与中央组织关系图",
              purpose: "用非人物扮演的关系图呈现三大作风、看齐原则和中央组织结果。",
              representationLabel: "复原性创作·非档案影像",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("104—105", "124—125"),
          sensitivity: "standard",
          render: {
            kind: "organization_evidence_map",
            visualSummary:
              "三大作风卡、看齐原文和中央组织关系图共同说明团结统一，不复原具体投票过程。",
            interaction: {
              mode: "organization_evidence_map",
              learnerAction: "依次核对三大作风、看齐原文和组织关系节点。",
              feedback: "归纳七大何以成为团结的大会、胜利的大会。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
              keyboardOperable: true,
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
      ],
      reviewState: "pending_review",
      reviewNotes: [],
    },
    {
      id: "M6S2",
      order: 2,
      title: "确立毛泽东思想为党的指导思想",
      identity: "抗战史料研学员",
      timeLabel: "1945年4—6月·七大审议并通过党章阶段",
      mainVenue: "延安杨家岭中央大礼堂",
      archiveSlices: ["《关于修改党章的报告》", "七大党章档案"],
      pageRefs: [{ print: "105—106", pdf: "125—126" }],
      lectureBudgetSeconds: 180,
      formalTaskIds: [],
      sequence: ["M6S2_N01", "M6S2_N02", "M6S2_N03"],
      localExtensionIds: [],
      nodes: [
        {
          id: "M6S2_N01",
          sceneId: "M6S2",
          order: 10,
          nodeType: "archive",
          title: "把毛泽东思想确立为党的指导思想并写入党章",
          timeAnchor: "1945年4—6月",
          estimatedSeconds: 55,
          claimId: "M6S2_CLAIM_GUIDING_THOUGHT_WRITTEN_IN_PARTY_CONSTITUTION",
          claim: "把毛泽东思想确立为党的指导思想并写入党章，是党的七大的历史性贡献。",
          evidencePoints: [
            "七大把毛泽东思想确立为党的指导思想并写入党章。",
            "刘少奇在《关于修改党章的报告》中阐明毛泽东思想同马克思列宁主义理论和中国革命实践的关系。",
          ],
          verbatimQuotes: [
            {
              text: "毛泽东思想，就是马克思列宁主义的理论与中国革命的实践之统一的思想，就是中国的共产主义，中国的马克思主义。",
              context: "刘少奇《关于修改党章的报告》",
              verbatim: true,
            },
          ],
          assetSuggestions: [
            {
              id: "AST-M6S2-SUG-001",
              kind: "interface",
              label: "党章指导思想档案页",
              purpose: "并列呈现历史性贡献判断与《关于修改党章的报告》原文证据。",
              representationLabel: "教材载述史料·非档案原件",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("105", "125"),
          sensitivity: "standard",
          render: {
            kind: "theory_document_reader",
            visualSummary: "党章档案页与报告原文双栏呈现，避免虚构具体手稿或审议对白。",
            interaction: {
              mode: "document_evidence_reader",
              learnerAction: "对照党章结论与报告原文，标记理论与实践相统一的关键语义。",
              feedback: "确认指导思想写入党章与报告阐释之间的证据关系。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
              keyboardOperable: true,
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
        {
          id: "M6S2_N02",
          sceneId: "M6S2",
          order: 20,
          nodeType: "lecture",
          title: "毛泽东思想的主要内容",
          timeAnchor: "1945年4—6月",
          estimatedSeconds: 65,
          claimId: "M6S2_CLAIM_MAIN_CONTENTS",
          claim: "《关于修改党章的报告》概括毛泽东思想的主要内容。",
          evidencePoints: [
            "关于现代世界情况及中国国情的分析。",
            "关于新民主主义、解放农民、革命统一战线、革命战争和革命根据地的理论与政策。",
            "关于建设新民主主义共和国、建设党和文化的理论与政策等。",
          ],
          verbatimQuotes: [],
          assetSuggestions: [
            {
              id: "AST-M6S2-SUG-002",
              kind: "interface",
              label: "毛泽东思想主要内容九域矩阵",
              purpose: "按教材列举的九个方面分组展开，不压缩成未经教材表述的新口号。",
              representationLabel: "教材载述史料·非档案原件",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("105—106", "125—126"),
          sensitivity: "standard",
          render: {
            kind: "theory_scope_matrix",
            visualSummary: "九个理论与政策范围按分析基础、革命实践、国家与党的建设分层显示。",
            interaction: {
              mode: "theory_scope_matrix",
              learnerAction: "展开九个范围卡并按教材原有语义核验其归属。",
              feedback: "形成毛泽东思想主要内容的结构化全貌，不产生计分任务。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
              keyboardOperable: true,
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
        {
          id: "M6S2_N03",
          sceneId: "M6S2",
          order: 30,
          nodeType: "lecture",
          title: "历史意义",
          timeAnchor: "七大及其后",
          estimatedSeconds: 60,
          claimId: "M6S2_CLAIM_COLLECTIVE_WISDOM_AND_FIRST_LEAP",
          claim:
            "毛泽东思想是中国共产党集体智慧的结晶，实现马克思主义中国化第一次历史性飞跃，七大后全党据此为抗战最后胜利和新民主主义革命胜利英勇奋斗。",
          evidencePoints: [
            "确立毛泽东思想为党的指导思想，是近代中国历史和人民革命斗争发展的必然选择。",
            "毛泽东思想在党领导人民艰苦奋斗、总结正反经验的实践中逐步形成，是中国共产党集体智慧的结晶。",
            "毛泽东思想以独创性理论丰富和发展马克思主义，实现马克思主义中国化第一次历史性飞跃，毛泽东是其伟大开拓者。",
            "七大之后，全党在毛泽东思想指引下团结一致，为夺取抗战最后胜利和新民主主义革命全国胜利英勇奋斗。",
          ],
          verbatimQuotes: [],
          assetSuggestions: [
            {
              id: "AST-M6S2-SUG-003",
              kind: "programmatic_2_5d",
              label: "形成—结晶—飞跃—实践意义链",
              purpose: "以因果关系链呈现形成基础、集体智慧、理论飞跃和实践作用。",
              representationLabel: "复原性创作·非档案影像",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("106", "126"),
          sensitivity: "standard",
          render: {
            kind: "historical_significance_chain",
            visualSummary: "四段证据链从历史选择通向集体智慧、第一次历史性飞跃与七大后的共同实践。",
            interaction: {
              mode: "historical_significance_chain",
              learnerAction: "按因果次序连接形成基础、理论性质、历史飞跃和实践作用。",
              feedback: "系统归纳确立指导思想的历史意义；本场景不生成正式任务。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
              keyboardOperable: true,
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
      ],
      reviewState: "pending_review",
      reviewNotes: [],
    },
    {
      id: "M6S3",
      order: 3,
      title: "全面反攻与抗日战争的伟大胜利",
      identity: "抗战史料研学员",
      timeLabel: "1945年上半年—10月25日，重点8月9日、8月15日、9月2日、9月3日、10月25日",
      mainVenue: "延安总部、中国东北、东京湾和台湾台北",
      archiveSlices: [],
      pageRefs: [{ print: "106—110", pdf: "126—130" }],
      lectureBudgetSeconds: 240,
      formalTaskIds: ["T19"],
      taskAnchorNodeId: "M6S3_N02",
      sequence: ["M6S3_N01", "M6S3_N02", "M6S3_N03", "M6S3_N04", "T19"],
      localExtensionIds: [],
      nodes: [
        {
          id: "M6S3_N01",
          sceneId: "M6S3",
          order: 10,
          nodeType: "archive",
          title: "世界反法西斯战争胜利进程与全面反攻",
          timeAnchor: "1945年上半年—8月9日",
          estimatedSeconds: 55,
          claimId: "M6S3_CLAIM_COMPREHENSIVE_COUNTEROFFENSIVE",
          claim:
            "世界反法西斯战争进入最后胜利阶段后，中国共产党领导的敌后军民根据延安总部指示和命令发动猛烈全面反攻，很快解放大片国土。",
          evidencePoints: [
            "1945年上半年，世界反法西斯战争进入最后胜利阶段；4月联合国制宪会议在旧金山举行，包括解放区代表董必武在内的中国代表团出席。",
            "8月9日，苏联红军开赴中国东北战场，同中国军民一道对日作战；毛泽东同日发表《对日寇的最后一战》。",
            "随后，延安总部朱德总司令发布七道全面反攻命令，中国抗日战争进入全面反攻阶段。",
            "各抗日根据地军民向日、伪军发起猛烈反攻，很快解放大片国土。",
          ],
          verbatimQuotes: [],
          assetSuggestions: [
            {
              id: "AST-M6S3-SUG-001",
              kind: "programmatic_2_5d",
              label: "1945年全面反攻证据地图",
              purpose:
                "参考教材战略反攻形势图，以分层箭头表现敌后军民反攻，不制作可改写战果的作战游戏。",
              representationLabel: "复原性创作·非档案影像",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("106—108", "126—128"),
          sensitivity: "standard",
          render: {
            kind: "counteroffensive_map",
            visualSummary:
              "国际进程、8月9日声明、七道命令和教材反攻形势图按层展开，明确国际力量与中国军民的共同背景。",
            interaction: {
              mode: "counteroffensive_map",
              learnerAction: "沿时间轴开启国际背景、延安总部命令和各根据地反攻图层。",
              feedback: "说明全面反攻如何在有利国际形势与中国长期抗战基础上展开。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
              keyboardOperable: true,
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
        {
          id: "M6S3_N02",
          sceneId: "M6S3",
          order: 20,
          nodeType: "archive",
          title: "抗日战争的伟大胜利",
          timeAnchor: "1945年8月9日—10月25日",
          estimatedSeconds: 65,
          claimId: "M6S3_CLAIM_VICTORY_DATES_DISTINGUISHED",
          claim:
            "理清8月15日《终战诏书》广播、9月2日日本代表在投降书签字、9月3日抗战胜利纪念日和10月25日中国政府在台湾举行受降仪式。",
          evidencePoints: [
            "8月15日，日本天皇裕仁以广播形式发布《终战诏书》，日本无条件投降。",
            "9月2日，日本代表在投降书上签字，侵华日军128万人向中国投降；中国抗日战争和世界反法西斯战争胜利结束。",
            "日本代表签字的第二日即9月3日，成为中国人民抗日战争胜利纪念日；纪念日不是投降书签字日。",
            "10月25日，中国政府在台湾举行受降仪式；被日本占领50年之久的台湾以及澎湖列岛重归中国主权管辖之下，成为抗战取得完全胜利的重要标志。",
          ],
          verbatimQuotes: [],
          dateMarkers: [
            {
              date: "1945-08-09",
              category: "counteroffensive",
              label: "毛泽东发表《对日寇的最后一战》，延安总部随后发布全面反攻命令",
            },
            {
              date: "1945-08-15",
              category: "surrender_broadcast",
              label: "日本天皇以广播形式发布《终战诏书》，日本无条件投降",
            },
            {
              date: "1945-09-02",
              category: "surrender_signature",
              label: "日本代表在投降书上签字",
            },
            {
              date: "1945-09-03",
              category: "victory_memorial_day",
              label: "日本代表签字的第二日，成为中国人民抗日战争胜利纪念日",
            },
            {
              date: "1945-10-25",
              category: "taiwan_surrender_ceremony",
              label: "中国政府在台湾举行受降仪式",
            },
          ],
          assetSuggestions: [
            {
              id: "AST-M6S3-SUG-002",
              kind: "interface",
              label: "五节点胜利日期语义时间线",
              purpose:
                "用颜色、图标和事件类型区分反攻、广播、签字、纪念与台湾受降，9月3日只作史实标记。",
              representationLabel: "教材载述史料·非档案原件",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("108", "128"),
          sensitivity: "standard",
          render: {
            kind: "victory_date_timeline",
            visualSummary: "五个日期使用不同语义标签，禁止把9月3日纪念日显示成9月2日签字事件。",
            interaction: {
              mode: "victory_date_timeline",
              learnerAction: "逐项核验日期、行为主体、事件性质与历史意义，再进入T19拖拽排序。",
              feedback:
                "T19仅拖拽8月9日、8月15日、9月2日、10月25日；9月3日保留为不可拖拽的纪念日校准标记。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
              keyboardOperable: true,
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
        {
          id: "M6S3_N03",
          sceneId: "M6S3",
          order: 30,
          nodeType: "witness",
          title: "历史意义",
          timeAnchor: "1931—1945年抗战历程及胜利总结",
          estimatedSeconds: 55,
          claimId: "M6S3_CLAIM_SACRIFICE_AND_HISTORICAL_SIGNIFICANCE",
          claim:
            "中国人民抗日战争付出巨大牺牲并取得近代以来反抗外敌入侵的第一次完全胜利，成为民族复兴的历史转折点并产生深远世界意义。",
          evidencePoints: [
            "中国人民抗日战争是近代以来持续时间最长、规模最大、牺牲最多的民族解放斗争，也是第一次取得完全胜利的民族解放斗争。",
            "据不完全统计，整个战争期间中国军民伤亡3500多万人；按1937年比值折算，直接经济损失1000多亿美元、间接经济损失5000多亿美元。",
            "胜利彻底打败日本侵略者，捍卫国家主权和领土完整，洗刷近代以来抗击外来侵略屡战屡败的民族耻辱。",
            "胜利促进民族觉醒和大团结，为民族独立与人民解放奠定基础，并为世界反法西斯战争胜利和维护世界和平作出重大贡献，提高中国国际地位和影响。",
          ],
          verbatimQuotes: [],
          assetSuggestions: [
            {
              id: "AST-M6S3-SUG-003",
              kind: "interface",
              label: "抗战牺牲与胜利意义庄重统计墙",
              purpose:
                "以低刺激、高可读的统计与意义卡见证牺牲，不把伤亡、损失或胜利制作成娱乐化榜单。",
              representationLabel: "教材载述史料·非档案原件",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("108—109", "128—129"),
          sensitivity: "solemn_sacrifice",
          render: {
            kind: "solemn_statistical_witness",
            visualSummary:
              "伤亡与经济损失在庄重低动态界面中呈现，随后以国家、民族和世界三个层级说明胜利意义。",
            interaction: {
              mode: "solemn_statistical_witness",
              learnerAction: "自主翻阅统计注释与三层意义卡，不进行积分、竞猜或失败重来。",
              feedback: "引导学习者在看见巨大牺牲的基础上理解胜利的历史转折意义。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
              keyboardOperable: true,
            },
            guardrails: sacrificeGuardrails,
          },
          reviewState: "pending_review",
        },
        {
          id: "M6S3_N04",
          sceneId: "M6S3",
          order: 40,
          nodeType: "lecture",
          title: "中流砥柱、决定性因素与伟大抗战精神",
          timeAnchor: "全民族抗战与胜利总结",
          estimatedSeconds: 65,
          claimId: "M6S3_CLAIM_CENTRAL_PILLAR_DECISIVE_FACTOR_AND_SPIRIT",
          claim: "中国共产党在全民族抗战中发挥中流砥柱作用，是抗战取得完全胜利的决定性因素。",
          evidencePoints: [
            "中国共产党以卓越政治领导力和正确战略策略指引抗战方向，推动全民族坚持抗战、团结、进步，维护团结抗战大局，成为全民族抗战的中流砥柱。",
            "抗战实践表明，中国共产党是领导中国人民争取民族独立和人民解放的坚强核心；教材据此作出中流砥柱作用是完全胜利决定性因素的判断。",
            "伟大抗战精神包括天下兴亡、匹夫有责的爱国情怀，视死如归、宁死不屈的民族气节，不畏强暴、血战到底的英雄气概，百折不挠、坚忍不拔的必胜信念。",
            "八路军、新四军和其他人民抗日武装对敌作战12.5万余次；到抗战结束时人民军队约132万人、民兵260余万人，抗日民主根据地即解放区19块，面积近100万平方公里、人口近1亿。",
            "敌后战场逐渐成为中国人民抗日战争的主战场，并为最终取得新民主主义革命胜利创造前所未有的有利条件。",
          ],
          verbatimQuotes: [],
          assetSuggestions: [
            {
              id: "AST-M6S3-SUG-004",
              kind: "programmatic_2_5d",
              label: "中流砥柱决定性因素证据链",
              purpose:
                "把政治领导、战略策略、人民战争、力量发展和抗战精神连成可追溯证据链，同时保留全民族与国际贡献语境。",
              representationLabel: "复原性创作·非档案影像",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("109—110", "129—130"),
          sensitivity: "standard",
          render: {
            kind: "central_pillar_evidence_chain",
            visualSummary:
              "政治领导、统一战线、敌后战场、人民力量和抗战精神五层证据汇聚到教材结论，旁列全民族与国际贡献语境。",
            interaction: {
              mode: "central_pillar_evidence_chain",
              learnerAction: "依次开启五层证据，核对每层如何支撑中流砥柱与决定性因素判断。",
              feedback:
                "形成模块六和全章的结论性证据链，避免把决定性因素误写为排除其他力量的唯一原因。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
              keyboardOperable: true,
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
      ],
      reviewState: "pending_review",
      reviewNotes: [
        {
          id: "M6S3_REVIEW_T19_DATE_SCOPE",
          scope: "M6S3_N02与T19",
          state: "open",
          note: "教材时间线包含9月3日胜利纪念日；冻结的T19排序项仅含8月9日、8月15日、9月2日、10月25日。本蓝图把9月3日设为不可拖拽的语义校准标记，不修改T19。",
        },
        {
          id: "M6S3_REVIEW_C06_ZERO_LOAD",
          scope: "M6S3主体节点与T19之间",
          state: "open",
          note: "C06已确认移除的地方史拓展不进入节点、序列、素材建议或档案切片；主体知识节点结束后直接进入T19。",
        },
      ],
    },
  ],
  reviewState: "pending_review",
  reviewNotes: [
    {
      id: "M6_REVIEW_PAGE_REFERENCE",
      scope: "M6全部节点",
      state: "open",
      note: "原始DeepSeek方案把源PDF第123—130页误称为书中页码；本蓝图按书内印刷103—110页 / 源PDF123—130页双字段记录，不改教材正文或正式任务。",
    },
    {
      id: "M6_REVIEW_DURATION_CONFLICT",
      scope: "M6标准路线",
      state: "open",
      note: "原方案模块总时长写约10分钟，但三场景知识讲授标注合计约11分钟；本蓝图暂保留逐场景240/180/240秒估算，待普通话旁白、交互与转场实测后再提交时长取舍，不通过删减史实或加速朗读解决。",
    },
    {
      id: "M6_REVIEW_TASK_PAGE_REFS",
      scope: "T18与T19",
      state: "open",
      note: "运行时T18、T19当前未登记双页码；本蓝图通过M6S1_N02与M6S3_N02登记教材证据锚点，按本任务边界不修改正式题目数据。",
    },
  ],
} satisfies M6Blueprint;
